
import { useState, useEffect } from "react";
import { Industry, OrganizationDetails, OrganizationSize, OrganizationType, AssessorRole } from "@/types/assessment";
import { Button } from "@/components/ui/button";
import { useToast } from "@/hooks/use-toast";
import { Card } from "@/components/ui/card";
import OrganizationDetailsSection from "./form/OrganizationDetailsSection";
import AssessorDetailsSection from "./form/AssessorDetailsSection";
import { useAuth } from "@/contexts/AuthContext";

interface Props {
  onSubmit: (details: OrganizationDetails) => void;
}

const NewOrganizationForm = ({ onSubmit }: Props) => {
  const { toast } = useToast();
  const { user } = useAuth();
  
  const [formState, setFormState] = useState({
    name: "",
    industry: "" as Industry,
    size: "" as OrganizationSize,
    country: "",
    organizationType: "" as OrganizationType,
    assessorName: "",
    assessorRole: "" as AssessorRole,
    assessorEmail: ""
  });

  // Pre-fill the assessor details with user data if available
  useEffect(() => {
    if (user) {
      setFormState(prev => ({
        ...prev,
        assessorName: user.fullName || prev.assessorName,
        assessorEmail: user.email || prev.assessorEmail
      }));
    }
  }, [user]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    if (!formState.name || !formState.industry || !formState.size || 
        !formState.assessorName || !formState.assessorRole || !formState.assessorEmail) {
      toast({
        title: "Error",
        description: "Please fill in all required fields including assessor details",
        variant: "destructive"
      });
      return;
    }

    const details: OrganizationDetails = {
      name: formState.name.trim(),
      industry: formState.industry,
      size: formState.size,
      assessmentDate: new Date().toISOString(),
      country: formState.country,
      organizationType: formState.organizationType as OrganizationType,
      assessorName: formState.assessorName.trim(),
      assessorRole: formState.assessorRole,
      assessorEmail: formState.assessorEmail.trim()
    };

    onSubmit(details);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-12 px-4">
      <div className="max-w-md mx-auto">
        <div className="text-center mb-8">
          <img 
            src="/lovable-uploads/6ee33c0a-e1be-486d-a08f-29e42b3b1ae9.png"
            alt="AIMAA Logo"
            className="h-16 mx-auto mb-4"
          />
          <h1 className="text-2xl font-bold text-gray-900">
            AI Maturity Assessment & Alignment (AIMAA)
          </h1>
        </div>

        <Card className="bg-white rounded-lg shadow-lg p-6">
          <form onSubmit={handleSubmit} className="space-y-6">
            <OrganizationDetailsSection 
              formState={formState}
              setFormState={setFormState}
            />
            
            <AssessorDetailsSection 
              formState={formState}
              setFormState={setFormState}
            />

            <Button 
              type="submit"
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 px-4 rounded-lg"
            >
              Start Assessment
            </Button>
          </form>
        </Card>
      </div>
    </div>
  );
};

export default NewOrganizationForm;
